import { dialogFunction } from 'common/components';
import { upc12ToGTIN14 } from 'common/components/product-add/components/utils/validation';

// ─── Confirm dialog ───────────────────────────────────────────────────────────

export const confirmDialog = (content, width = 460) =>
  new Promise((resolve) => {
    dialogFunction({
      type: 'warn',
      content,
      cancelText: 'Cancel',
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
      zIndex: 1400,
      width,
    });
  });

// ─── AG Grid cell helpers ─────────────────────────────────────────────────────

export const revertCell = (gridRef, uniqueId, field, value) => {
  const rowNode = gridRef.current?.api?.getRowNode(uniqueId);
  if (rowNode) rowNode.setDataValue(field, value);
};

export const refreshCells = (gridRef, uniqueId, columns) => {
  setTimeout(() => {
    const rowNode = gridRef.current?.api?.getRowNode(uniqueId);
    if (rowNode) {
      gridRef.current.api.refreshCells({
        rowNodes: [rowNode],
        columns,
        force: true,
      });
    }
  }, 0);
};

// ─── Side effects ─────────────────────────────────────────────────────────────

export const getNoCodeCaseSideEffects = (newValue, liveProduct) => {
  if (newValue === true) {
    return {
      caseGtin: '',
      caseUpc12: '',
      validationErrors: (liveProduct.validationErrors ?? []).filter(
        (err) => err.field !== 'caseGtin' && err.field !== 'caseUpc12'
      ),
    };
  }
  return {};
};

// ─── Shipper items sync ───────────────────────────────────────────────────────
export const syncShipperItems = (
  products,
  uniqueId,
  validatedProduct,
  params
) => {
  const {
    colDef: { field },
    oldValue,
    newValue,
  } = params;

  // Shipper items now match on caseGtin (the Display/Shipper GTIN),
  // not unitUpc. Sync when caseGtin changes or description fields change.
  const currentGtin = validatedProduct.caseGtin;

  const needsGtinSync = field === 'caseGtin' && oldValue !== newValue;
  const needsDescSync =
    (field === 'productName' || field === 'productDescription') && currentGtin;

  if (!needsGtinSync && !needsDescSync) {
    return products.map((p) =>
      p.uniqueId === uniqueId ? validatedProduct : p
    );
  }

  return products.map((p) => {
    if (p.uniqueId === uniqueId) return validatedProduct;

    const items = p.displayShipperItems;
    if (!items?.length) return p;

    let changed = false;
    const updatedItems = items.map((item) => {
      let next = item;

      if (needsGtinSync && item.itemUpc === oldValue) {
        const caseGtinError = (validatedProduct.validationErrors ?? []).find(
          (e) => e.field === 'caseGtin' && checkErrorType(e.messageType)
        );

        next = {
          ...next,
          itemUpc: newValue,
          validationErrors: [
            ...(next.validationErrors ?? []).filter(
              (e) => e.field !== 'itemUpc'
            ),
            ...(caseGtinError
              ? [
                  {
                    field: 'itemUpc',
                    message: caseGtinError.message,
                    messageType: 'Error',
                  },
                ]
              : []),
          ],
        };
        changed = true;
      }

      if (needsDescSync && item.itemUpc === currentGtin) {
        next = {
          ...next,
          itemDescription:
            field === 'productName'
              ? newValue
              : validatedProduct.productDescription,
        };
        changed = true;
      }

      return next;
    });

    return changed ? { ...p, displayShipperItems: updatedItems } : p;
  });
};

export const checkErrorType = (errorMessage) => {
  return errorMessage?.toLowerCase() === 'error';
};

export const checkWarningType = (errorMessage) => {
  return errorMessage?.toLowerCase() === 'warning';
};

const findValidationError = (errors, fieldName) =>
  errors.find((e) => e?.field?.toLowerCase() === fieldName.toLowerCase());

const filterOutError = (errors, ...fields) =>
  errors.filter((e) => !fields.includes(e?.field?.toLowerCase()));

const isCaseGtinRequiredError = (e) =>
  e?.field?.toLowerCase() === 'casegtin' &&
  e?.message?.toLowerCase().includes('required');

const isCaseUpc12RequiredError = (e) =>
  e?.field?.toLowerCase() === 'caseupc12' &&
  e?.message?.toLowerCase().includes('required');

export const computeUpdatedErrors = ({
  field,
  newValue,
  liveProduct,
  revalidated,
}) => {
  const existing = liveProduct.validationErrors ?? [];
  const revalidatedErrors = revalidated.validationErrors ?? [];

  const hasCaseUpc12 = !!liveProduct.caseUpc12?.trim();

  if (field === 'variableWeight') {
    const clearedFields = [
      'variableweight',
      'unitupc',
      'nocodecase',
      'innerpackupc',
      'innerpackgtin',
      'innerpackpacksize',
      'innerpacksellable',
      'caseinnerpackcount',
    ];
    const isClearedFieldError = (e) =>
      clearedFields.includes(e?.field?.toLowerCase());
    // Case codes keep their values, so server-side errors (e.g. "already
    // exists") must survive; only their required-ness depends on this toggle.
    const isCaseCodeRequiredError = (e) =>
      isCaseGtinRequiredError(e) || isCaseUpc12RequiredError(e);
    const isRecomputed = (e) =>
      isClearedFieldError(e) || isCaseCodeRequiredError(e);

    return [
      ...existing.filter((e) => !isRecomputed(e)),
      ...revalidatedErrors.filter(isRecomputed),
    ];
  }

  if (field === 'noCodeCase') {
    // Remove all caseGtin errors (field fully disabled) + only the required
    // caseUpc12 error (format/exists errors on caseUpc12 remain visible).
    let errors = filterOutError(existing, 'casegtin', 'caseupc12');
    if (newValue === false) {
      const caseGtinErr = findValidationError(revalidatedErrors, 'casegtin');
      if (caseGtinErr && !hasCaseUpc12) errors.push(caseGtinErr);
      const caseUpc12RequiredErr = revalidatedErrors.find(
        isCaseUpc12RequiredError
      );
      if (caseUpc12RequiredErr) errors.push(caseUpc12RequiredErr);
    }
    return errors;
  }

  if (field === 'innerPackSellable') {
    // innerPackUpc's required-ness and innerPackGtin's disabled/cleared state
    // both depend on innerPackSellable, so their errors must be recomputed
    // whenever innerPackSellable changes.
    let errors = filterOutError(
      existing,
      'innerpacksellable',
      'innerpackupc',
      'innerpackgtin'
    );
    const innerPackUpcErr = findValidationError(revalidatedErrors, 'innerPackUpc');
    if (innerPackUpcErr) errors.push(innerPackUpcErr);
    const innerPackGtinErr = findValidationError(revalidatedErrors, 'innerPackGtin');
    if (innerPackGtinErr) errors.push(innerPackGtinErr);
    return errors;
  }

  // Regular field: replace only this field's error
  let errors = filterOutError(existing, field.toLowerCase());
  const fieldError = findValidationError(revalidatedErrors, field);
  const skipCaseGtinError =
    field === 'caseGtin' && hasCaseUpc12 && isCaseGtinRequiredError(fieldError);
  if (fieldError && !skipCaseGtinError) errors.push(fieldError);

  // caseUpc12: only clear/restore the caseGtin "required" error;
  // format and server-side errors (e.g. "already exists") remain visible.
  if (field === 'caseUpc12') {
    errors = errors.filter((e) => !isCaseGtinRequiredError(e));
    if (!newValue?.trim()) {
      const caseGtinRequiredError = revalidatedErrors.find(
        isCaseGtinRequiredError
      );
      if (caseGtinRequiredError) errors.push(caseGtinRequiredError);
    }
  }

  return errors;
};

// ─── Auto-fill caseGtin from a valid caseUpc12 ───────────────────────────────

/**
 * Returns `{ caseGtin }` to spread into the updated product when all conditions
 * for auto-populating Case GTIN from Case UPC-12 are met, otherwise `{}`.
 *
 * Conditions:
 *  - The edited field is `caseUpc12`
 *  - `caseGtin` is currently empty (never overwrites an existing value)
 *  - The new `caseUpc12` value has no Error in the revalidated result
 *  - `upc12ToGTIN14` can produce a valid 14-digit GTIN from the new value
 */
export const resolveCaseGtinAutoFill = ({
  field,
  newValue,
  liveProduct,
  revalidated,
}) => {
  if (field !== 'caseUpc12') return {};
  if (liveProduct.caseGtin?.trim()) return {};
  const hasError = (revalidated.validationErrors ?? []).some(
    (e) => e.field === 'caseUpc12' && e.messageType === 'Error'
  );
  if (hasError) return {};
  const gtin = upc12ToGTIN14(newValue);
  return gtin ? { caseGtin: gtin } : {};
};
